/**
 * ProviderCoverageBanner — R-71 wizard surface when provider coverage
 * blocks report dispatch. Offers Retry / Override / Cancel paths.
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import type { ProviderCoverageReport } from '../../lib/audit/providerCoverageGate';
import { resolveProviderStatusFromSed } from '../../lib/audit/providerCoverageGate';
import { PROVIDER_LABELS } from '../../config/reportRequiredProviders';
import { useStore } from '../../store';
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';

export interface ProviderCoverageBannerProps {
  siteId: string;
  reportTypeId: ReportTypeId;
  report: ProviderCoverageReport;
  sed?: SiteEvidenceDocument | null;
  onRetryDispatch: (opts?: { providerCoverageDispatchWaivers?: { providerId: string }[] }) => void;
  onCancel?: () => void;
  testId?: string;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'success': return 'Available';
    case 'baseline': return 'Baseline defaults';
    case 'unavailable': return 'Unavailable';
    case 'failed': return 'Failed validation';
    case 'missing': return 'Not collected';
    case 'skipped': return 'Skipped';
    default: return status;
  }
}

export function ProviderCoverageBanner({
  siteId,
  reportTypeId,
  report,
  sed,
  onRetryDispatch,
  onCancel,
  testId = 'provider-coverage-banner',
}: ProviderCoverageBannerProps) {
  const retryProviderFetch = useStore(s => s.retryProviderFetch);
  const recordAuditOverride = useStore(s => s.recordAuditOverride);

  const blocking = report.missingRequired;
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  if (blocking.length === 0) return null;

  const handleRetryAll = async () => {
    setBusy(true);
    try {
      for (const item of blocking) {
        setRetryingId(item.providerId);
        await retryProviderFetch(siteId, item.providerId);
      }
      onRetryDispatch();
    } finally {
      setBusy(false);
      setRetryingId(null);
    }
  };

  const handleRetryOne = async (providerId: string) => {
    setRetryingId(providerId);
    setBusy(true);
    try {
      await retryProviderFetch(siteId, providerId);
      onRetryDispatch();
    } finally {
      setBusy(false);
      setRetryingId(null);
    }
  };

  const handleOverride = async () => {
    if (rationale.trim().length < 40) return;
    setBusy(true);
    try {
      const providerIds = blocking.map(b => b.providerId);
      await recordAuditOverride(siteId, {
        type: 'coverage',
        fields: providerIds,
        rationale: rationale.trim(),
        reportTypeId,
      });
      const { logAuditEvent } = await import('../../lib/telemetry/auditTelemetry');
      logAuditEvent({
        type: 'coverage-gate-override',
        siteId,
        reportType: reportTypeId,
        payload: { providerIds, rationaleLength: rationale.trim().length },
      });
      setShowOverrideModal(false);
      setRationale('');
      onRetryDispatch({
        providerCoverageDispatchWaivers: providerIds.map(providerId => ({ providerId })),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-destructive/40 bg-destructive/5 p-4 space-y-3"
      data-testid={testId}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1">
          <div className="type-body font-bold uppercase tracking-widest text-destructive">
            Required data providers missing
          </div>
          <p className="text-[11px] text-text-secondary leading-relaxed">
            Report generation was blocked because required providers did not return usable data.
            Retry the fetch or override with rationale to proceed.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {blocking.map(item => {
          const live = sed ? resolveProviderStatusFromSed(item.providerId, sed) : null;
          const label = PROVIDER_LABELS[item.providerId] ?? item.providerLabel;
          const status = live?.status ?? item.status;
          return (
            <div
              key={item.providerId}
              className="flex flex-wrap items-start justify-between gap-2 border border-border/60 bg-background p-2 text-[11px]"
              data-testid={`provider-coverage-item-${item.providerId}`}
            >
              <div className="space-y-0.5">
                <div className="font-medium text-text-primary">{label}</div>
                <div className="text-text-secondary">{item.message}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className="type-meta uppercase tracking-widest text-destructive"
                  data-testid={`provider-coverage-status-${item.providerId}`}
                >
                  {statusLabel(status)}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleRetryOne(item.providerId)}
                  data-testid={`provider-coverage-retry-${item.providerId}`}
                >
                  {retryingId === item.providerId ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="default"
          disabled={busy}
          onClick={() => void handleRetryAll()}
          data-testid="provider-coverage-retry-all"
        >
          {busy && !retryingId ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Retry fetch
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setShowOverrideModal(true)}
          data-testid="provider-coverage-override-btn"
        >
          Override with rationale
        </Button>
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
            data-testid="provider-coverage-cancel-btn"
          >
            Cancel
          </Button>
        )}
      </div>

      {showOverrideModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
          data-testid="provider-coverage-override-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="provider-coverage-override-title"
        >
          <div className="w-full max-w-md bg-background border border-border p-4 space-y-3 shadow-lg">
            <div id="provider-coverage-override-title" className="type-body font-bold uppercase tracking-widest">
              Override with rationale
            </div>
            <p className="text-[11px] text-text-secondary">
              Explain why dispatch should proceed despite missing provider data. This unblocks this run only
              (minimum 40 characters).
            </p>
            <textarea
              className="w-full min-h-[100px] border border-border p-2 text-sm"
              value={rationale}
              onChange={e => setRationale(e.target.value)}
              data-testid="provider-coverage-override-rationale"
              placeholder="Document why proceeding without this provider is acceptable for this dispatch…"
            />
            <div className="text-[10px] text-text-muted">
              {rationale.trim().length}/40 characters minimum
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowOverrideModal(false);
                  setRationale('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                disabled={busy || rationale.trim().length < 40}
                onClick={() => void handleOverride()}
                data-testid="provider-coverage-override-confirm"
              >
                Confirm override
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
